import { useState, useRef } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FiCheck } from 'react-icons/fi';
import SelectTextField from '../../molecules/SelectTextField';
import TextEdit from '../../molecules/TextEdit';
import { formatCurrency } from './collateralUtils';

const ALL_PROCESSES = ['XML', 'DRAWEE', 'INVOICE_PDF', 'PURCHASE_CONTRACT', 'RECEIPT_STUB', 'SEFAZ', 'ACCEPTED_BILL'];
const PROCESS_LABELS: Record<string, string> = {
    XML: 'XML', DRAWEE: 'Sacado', INVOICE_PDF: 'NF PDF',
    PURCHASE_CONTRACT: 'Contrato C/V', RECEIPT_STUB: 'Canhoto',
    SEFAZ: 'SEFAZ', ACCEPTED_BILL: 'Duplicata',
};

export interface CheckPayload {
    criteria_type: string;
    received?: boolean;
    status?: string;
    observation?: string;
}

export interface ValidationPanelResult {
    checks: CheckPayload[];
    receivableStatus?: string;
    receivableObservation?: string;
}

interface ValidationPanelProps {
    titles: any[];
    enabledProcesses: string[];
    editingIds: string[];
    selectedIds: Set<string>;
    initialProcess: Record<string, { received: string; status: string; observation: string }>;
    initialStatus: string;
    initialObservation: string;
    isSaving: boolean;
    onClose: () => void;
    onSave: (result: ValidationPanelResult) => void;
}

const MIXED = '__MIXED__';

const ValidationPanel = ({ titles, enabledProcesses, editingIds, selectedIds, initialProcess, initialStatus, initialObservation, isSaving, onClose, onSave }: ValidationPanelProps) => {
    const [batchProcess, setBatchProcess] = useState(initialProcess);
    const [batchStatus, setBatchStatus] = useState(initialStatus);
    const [batchObservation, setBatchObservation] = useState(initialObservation);

    // Track which fields user actually changed
    const dirty = useRef<Set<string>>(new Set());
    const markDirty = (key: string) => { dirty.current.add(key); };

    const selectedTitles = titles.filter((t: any) => editingIds.length > 0 ? editingIds.includes(t.id) : selectedIds.has(t.id));

    const handleSave = () => {
        const checks: CheckPayload[] = [];
        for (const p of ALL_PROCESSES.filter(proc => enabledProcesses.includes(proc) && proc !== 'DRAWEE')) {
            const receivedDirty = dirty.current.has(`${p}.received`);
            const statusDirty = dirty.current.has(`${p}.status`);
            const obsDirty = dirty.current.has(`${p}.observation`);
            if (!receivedDirty && !statusDirty && !obsDirty) continue;
            const payload: CheckPayload = { criteria_type: p };
            if (receivedDirty) payload.received = batchProcess[p]?.received ? batchProcess[p].received === 'true' : undefined;
            if (statusDirty) payload.status = batchProcess[p]?.status || undefined;
            if (obsDirty) payload.observation = batchProcess[p]?.observation || undefined;
            checks.push(payload);
        }
        const result: ValidationPanelResult = { checks };
        if (dirty.current.has('receivableStatus')) result.receivableStatus = batchStatus || undefined;
        if (dirty.current.has('receivableObservation')) result.receivableObservation = batchObservation || undefined;
        onSave(result);
    };

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/30" onClick={onClose} />
            <button onClick={onClose}
                className="fixed top-[15px] right-[585px] w-8 h-8 rounded-full shadow-lg z-[9999] flex items-center justify-center border border-gray-300 dark:border-slate-600 bg-gray-500 hover:bg-gray-600 text-white">
                <IoMdClose size={16} />
            </button>
            <div className="w-[600px] h-full bg-white dark:bg-slate-800 shadow-xl overflow-y-auto border-l border-gray-200 dark:border-slate-700">
                <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-10">
                    <span className="text-sm font-semibold dark:text-white">{editingIds.length > 0 ? 'Editar validação' : `Editar validação (${selectedIds.size})`}</span>
                    <button onClick={handleSave} disabled={isSaving}
                        className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs flex items-center space-x-1 disabled:opacity-50">
                        <FiCheck size={12} /> <span>{isSaving ? 'Salvando...' : 'Salvar'}</span>
                    </button>
                </div>
                <div className="p-4 space-y-4">
                    <div className="bg-gray-50 dark:bg-slate-900 rounded p-3 border border-gray-100 dark:border-slate-700">
                        <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Recebíveis selecionados</div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                            {selectedTitles.map((t: any) => (
                                <div key={t.id} className="flex justify-between text-xs">
                                    <span className="dark:text-white">{t.titleNumber} - {t.debtorName}</span>
                                    <span className="text-gray-500">{formatCurrency(t.amount)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2">Validações por processo</div>
                    <div className="grid grid-cols-2 gap-3">
                        {ALL_PROCESSES.filter(p => enabledProcesses.includes(p) && p !== 'DRAWEE').map(p => {
                            const received = batchProcess[p]?.received;
                            const status = batchProcess[p]?.status;
                            const topBorder = status === 'VALIDATED' ? 'border-t-green-500'
                                : status === 'REJECTED' ? 'border-t-red-500'
                                    : status === 'PENDING' ? 'border-t-amber-400'
                                        : received === 'true' ? 'border-t-blue-500'
                                            : 'border-t-gray-300 dark:border-t-slate-600';
                            return (
                                <div key={p} className={`border border-gray-200 dark:border-slate-700 border-t-4 ${topBorder} rounded p-3 space-y-2`}>
                                    <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase">{PROCESS_LABELS[p]}</span>
                                    <SelectTextField label="Recebido" value={received === MIXED ? '' : (received || '')}
                                        emptyLabel={received === MIXED ? 'Múltiplos valores' : undefined}
                                        items={[{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }]}
                                        onChange={(e: any) => { markDirty(`${p}.received`); setBatchProcess(prev => ({ ...prev, [p]: { ...prev[p], received: e.target.value, status: prev[p]?.status || '', observation: prev[p]?.observation || '' } })); }} />
                                    <SelectTextField label="Status" value={status === MIXED ? '' : (status || '')}
                                        emptyLabel={status === MIXED ? 'Múltiplos valores' : undefined}
                                        items={[{ value: 'PENDING', label: 'Pendente' }, { value: 'VALIDATED', label: 'Validado' }, { value: 'REJECTED', label: 'Rejeitado' }]}
                                        onChange={(e: any) => { markDirty(`${p}.status`); setBatchProcess(prev => ({ ...prev, [p]: { ...prev[p], received: prev[p]?.received || '', status: e.target.value, observation: prev[p]?.observation || '' } })); }} />
                                    <TextEdit label="Observação" value={batchProcess[p]?.observation === MIXED ? '' : (batchProcess[p]?.observation || '')}
                                        placeholder={batchProcess[p]?.observation === MIXED ? 'Múltiplos valores' : ''}
                                        onChange={(e: any) => { markDirty(`${p}.observation`); setBatchProcess(prev => ({ ...prev, [p]: { ...prev[p], received: prev[p]?.received || '', status: prev[p]?.status || '', observation: e.target.value } })); }} />
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2">Status do Recebível</div>
                    <div className="space-y-3">
                        <SelectTextField label="Status" value={batchStatus === MIXED ? '' : batchStatus}
                            emptyLabel={batchStatus === MIXED ? 'Múltiplos valores' : undefined}
                            items={[{ value: 'PENDING', label: 'Pendente' }, { value: 'OK', label: 'OK' }, { value: 'NOT_OK', label: 'Não OK' }]}
                            onChange={(e: any) => { markDirty('receivableStatus'); setBatchStatus(e.target.value); }} />
                        <TextEdit label="Observação" value={batchObservation === MIXED ? '' : batchObservation}
                            placeholder={batchObservation === MIXED ? 'Múltiplos valores' : ''}
                            onChange={(e: any) => { markDirty('receivableObservation'); setBatchObservation(e.target.value); }} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export { MIXED };
export default ValidationPanel;
